import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import VIDEO_URLS from '../utils/videourls' // .mp4: bg + avatares + videos (Safari/iPad)
import { ROUTES } from '../utils/constants'

// Detecta iPad/iOS Safari (incluye iPadOS)
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
const isIPadSafari = isIOS && isSafari

/** Tiempo máximo por vídeo en iPad (vídeos pesados + red lenta) */
const SAFARI_WARM_TIMEOUT_MS = 12000

/** Pocas descargas paralelas para no saturar HTTP/1.1 (6 conexiones/host) */
const WARMUP_FETCH_CONCURRENCY = 2

/** Verificación: toques a caché en lotes (antes: 40+ fetch a la vez) */
const VERIFY_FETCH_BATCH = 4

/** Evita fetch colgados indefinidamente (lectura desde disco/red lenta) */
const VERIFY_PER_URL_TIMEOUT_MS = 35000

/**
 * fetch con señal propia + timeout; respeta abort del padre (p. ej. al salir de la página).
 */
async function fetchWithTimeout(url, init = {}, parentSignal, timeoutMs = VERIFY_PER_URL_TIMEOUT_MS) {
  const ac = new AbortController()
  const onParentAbort = () => ac.abort()
  if (parentSignal) {
    if (parentSignal.aborted) throw new DOMException('Aborted', 'AbortError')
    parentSignal.addEventListener('abort', onParentAbort, { once: true })
  }
  const timer = setTimeout(() => ac.abort(), timeoutMs)
  try {
    return await fetch(url, { ...init, signal: ac.signal })
  } finally {
    clearTimeout(timer)
    if (parentSignal) parentSignal.removeEventListener('abort', onParentAbort)
  }
}

// Warmup de medios para Safari/iPad: usa <video> oculto + seeks
function warmOneSafari(url, onTick) {
  return new Promise((resolve) => {
    const v = document.createElement('video')
    v.src = url
    v.preload = 'auto'
    v.muted = true
    v.playsInline = true
    v.style.position = 'fixed'
    v.style.width = '1px'
    v.style.height = '1px'
    v.style.opacity = '0'
    v.style.pointerEvents = 'none'
    document.body.appendChild(v)

    let timeout
    let settled = false

    const isAlmostBuffered = () => {
      try {
        if (isNaN(v.duration) || !isFinite(v.duration) || v.duration === 0) return false
        if (v.buffered.length === 0) return false
        const end = v.buffered.end(v.buffered.length - 1)
        return end >= v.duration - 0.25
      } catch { return false }
    }

    const finish = (ok) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      try { v.pause() } catch {}
      try { v.remove() } catch {}
      onTick?.(1)
      resolve({ ok: !!ok })
    }

    v.addEventListener('error', () => finish(false), { once: true })

    v.addEventListener('loadedmetadata', async () => {
      try { await v.play().catch(() => {}) } catch {}
      const points = [0.0, 0.25, 0.5, 0.75, 0.98]
      for (const p of points) {
        if (isAlmostBuffered()) {
          finish(true)
          return
        }
        try { v.currentTime = Math.max(0, v.duration * p) } catch {}
        await new Promise(r => setTimeout(r, 600))
      }
      finish(isAlmostBuffered())
    }, { once: true })

    const tick = () => {
      if (isAlmostBuffered()) finish(true)
    }
    v.addEventListener('progress', tick)
    v.addEventListener('canplaythrough', () => finish(true))
    v.addEventListener('loadeddata', tick)
    v.addEventListener('ended', () => finish(true))

    timeout = setTimeout(() => finish(isAlmostBuffered()), SAFARI_WARM_TIMEOUT_MS)
    try { v.load() } catch {}
  })
}

// Orquesta en serie (Safari se lleva mal con mucha concurrencia)
async function warmupSafari(urls, onTick, isCancelled = () => false) {
  const failed = []
  for (const u of urls) {
    if (isCancelled()) break
    const { ok } = await warmOneSafari(u, onTick)
    if (!ok) failed.push(u)
  }
  return { failed }
}

// --- Config ---
const STORAGE_KEY = 'ermx_warmup_status_v1'

// Hash simple de la lista para saber si cambió la “versión” de contenidos
function hashString(s) {
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i)
  return (h >>> 0).toString(16)
}
const VERSION = hashString(VIDEO_URLS.join('|'))

// --- Descarga y drenado (materializa en caché) ---
async function fetchAndDrain(u, cacheMode = 'reload', signal) {
  const r = await fetch(u, {
    cache: cacheMode,
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  if (cacheMode === 'reload') {
    if (r.body && r.body.getReader) {
      const rd = r.body.getReader()
      while (true) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        const { done } = await rd.read()
        if (done) break
      }
    } else {
      await r.blob()
    }
  }
}

async function warmup(urls, { cacheMode = 'reload', concurrency = WARMUP_FETCH_CONCURRENCY, retries = 2, onTick, signal } = {}) {
  let i = 0
  const failed = []
  const worker = async () => {
    while (i < urls.length) {
      if (signal?.aborted) return
      const u = urls[i++]
      let ok = false, attempt = 0
      while (!ok && attempt <= retries) {
        if (signal?.aborted) return
        try {
          await fetchAndDrain(u, cacheMode, signal)
          ok = true
        } catch (e) {
          if (e?.name === 'AbortError') return
          attempt++
          if (attempt > retries) failed.push(u)
          else await new Promise(r => setTimeout(r, 500 * attempt)) // backoff
        } finally {
          onTick?.(1)
        }
      }
    }
  }
  await Promise.all(Array.from({ length: concurrency }, worker))
  return { failed }
}

// --- Verificación A: Resource Timing (sirve en Safari). Requiere TAO si es cross-origin ---
async function verifyByTiming(urls, { signal, onProgress } = {}) {
  const total = urls.length
  onProgress?.({ phase: 'timing', current: 0, total, detail: 'Iniciando lotes (force-cache)…' })

  // Asegura que caben todos los registros de timings
  if (performance.setResourceTimingBufferSize) {
    performance.setResourceTimingBufferSize(Math.max(300, urls.length * 3))
  }
  performance.clearResourceTimings()

  // “Toca” con force-cache en lotes (evita 40+ conexiones paralelas)
  for (let i = 0; i < urls.length; i += VERIFY_FETCH_BATCH) {
    if (signal?.aborted) {
      return { hits: [], miss: urls.slice(), unknown: [] }
    }
    const batch = urls.slice(i, i + VERIFY_FETCH_BATCH)
    await Promise.all(
      batch.map((u) =>
        fetchWithTimeout(u, { cache: 'force-cache' }, signal, VERIFY_PER_URL_TIMEOUT_MS).catch(() => {})
      )
    )
    const done = Math.min(i + batch.length, total)
    onProgress?.({
      phase: 'timing',
      current: done,
      total,
      detail: `Solicitudes a caché para medir tiempos: ${done} / ${total}`,
    })
  }
  onProgress?.({ phase: 'timing', current: total, total, detail: 'Interpretando resultados de red (HIT/MISS)…' })
  await new Promise((r) => setTimeout(r, 0)) // deja registrar timings

  const hits = [], miss = [], unknown = []
  for (const u of urls) {
    // ⚠️ Performance guarda ABSOLUTAS
    const abs = new URL(u, window.location.href).href

    // 1) Busca por nombre exacto (absoluto)
    let entries = performance.getEntriesByName(abs)

    // 2) Fallback (Safari a veces guarda con otra forma): filtramos por sufijo
    if (!entries || entries.length === 0) {
      const all = performance.getEntriesByType('resource')
      entries = all.filter(e => {
        if (!e || typeof e.name !== 'string') return false
        // e.name podría ser absoluta; probamos match por sufijo del path
        try {
          const nameUrl = new URL(e.name, window.location.href)
          const target = new URL(abs)
          return nameUrl.pathname === target.pathname
        } catch { return false }
      })
    }

    const e = entries && entries.length ? entries[entries.length - 1] : null
    if (!e) {
      unknown.push(u) // sin entrada, sin TAO o no registrado
      continue
    }

    // Heurística de HIT: transferSize === 0 con cuerpo > 0 (sirve en Safari/Chrome)
    if (e.encodedBodySize > 0 && e.transferSize === 0) hits.push(u)
    else miss.push(u)
  }

  return { hits, miss, unknown }
}


// --- Verificación B: only-if-cached (solo same-origin; no Safari) ---
async function verifyHttpCache(urls, { signal, onProgress } = {}) {
  const hits = [], miss = []
  const total = urls.length
  onProgress?.({ phase: 'http-cache', current: 0, total, detail: 'Comprobación directa en caché (uno a uno)…' })
  for (let idx = 0; idx < urls.length; idx++) {
    const u = urls[idx]
    if (signal?.aborted) break
    try {
      const res = await fetchWithTimeout(
        u,
        { cache: 'only-if-cached', mode: 'same-origin' },
        signal,
        VERIFY_PER_URL_TIMEOUT_MS
      )
      if (res.ok) hits.push(u)
      else miss.push(u)
    } catch {
      miss.push(u)
    }
    onProgress?.({
      phase: 'http-cache',
      current: idx + 1,
      total,
      detail: `Sólo-si-en-caché: ${idx + 1} / ${total}`,
    })
  }
  return { hits, miss }
}

export default function WarmupPage() {
  const navigate = useNavigate()
  const urls = useMemo(() => VIDEO_URLS.slice(), [])
  const total = urls.length

  const [done, setDone] = useState(0)
  const [failed, setFailed] = useState([])
  const [verified, setVerified] = useState(null) // {hits, miss, unknown?}
  const [verifying, setVerifying] = useState(false)
  /** Progreso tras barra al 100 %: fase timing | http-cache */
  const [verifyProgress, setVerifyProgress] = useState(null)
  const [verifyError, setVerifyError] = useState(null)

  // permite scroll aunque el body global tenga overflow hidden
  useEffect(() => {
    const prevBody = document.body.style.overflowY
    const prevHtml = document.documentElement.style.overflowY
    document.body.style.overflowY = 'auto'
    document.documentElement.style.overflowY = 'auto'
    return () => {
      document.body.style.overflowY = prevBody
      document.documentElement.style.overflowY = prevHtml
    }
  }, [])

useEffect(() => {
  let cancelled = false
  const ac = new AbortController()
  const { signal } = ac
  const isCancelled = () => cancelled || signal.aborted

  const params = new URLSearchParams(window.location.search)
  const force = params.get('force') === '1'

  const stored = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') } catch { return null }
  })()
  const alreadyWarm =
    !force && stored && stored.version === VERSION && stored.total === total && stored.ok

  const tick = (inc) => { if (!cancelled) setDone(d => d + inc) }
  const reportVerify = (p) => {
    if (!cancelled) {
      setVerifyError(null)
      setVerifyProgress(p)
    }
  }

  ;(async () => {
    try {
      setVerifyError(null)
      if (alreadyWarm) {
        setDone(total)
        const v = await doVerify(urls, signal, reportVerify)
        if (!isCancelled()) {
          setVerified(v)
          setVerifyProgress(null)
        }
        return
      }

      let failedUrls = []
      if (isIPadSafari) {
        const { failed: f } = await warmupSafari(urls, tick, isCancelled)
        failedUrls = f
      } else {
        const { failed: f } = await warmup(urls, {
          cacheMode: 'reload',
          concurrency: WARMUP_FETCH_CONCURRENCY,
          retries: 2,
          onTick: tick,
          signal,
        })
        failedUrls = f
      }

      if (!isCancelled()) setFailed(failedUrls)

      if (isCancelled()) return
      const v = await doVerify(urls, signal, reportVerify)
      if (isCancelled()) return
      setVerified(v)
      setVerifyProgress(null)
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: VERSION,
        total,
        ok: (v.hits?.length || 0) >= Math.floor(total * 0.9),
        finishedAt: new Date().toISOString()
      }))
    } catch (e) {
      if (!isCancelled()) {
        console.error(e)
        setVerifyError(e?.message || 'Error durante precarga o verificación')
        setVerifyProgress(null)
      }
    }
  })()

  return () => {
    cancelled = true
    ac.abort()
  }
}, [urls, total])


  async function doVerify(urls, signal, onProgress) {
    if (!urls.length) {
      onProgress?.({ phase: 'timing', current: 0, total: 0, detail: 'No hay archivos en la lista.' })
      return { hits: [], miss: [], unknown: [] }
    }
    // 1) Intenta Resource Timing
    const v = await verifyByTiming(urls, { signal, onProgress })
    // Si hay 'unknown', intenta fallback only-if-cached (igual dominio)
    const crossOriginOrNoTAO = v.unknown && v.unknown.length > 0
    if (crossOriginOrNoTAO) {
      onProgress?.({
        phase: 'http-cache',
        current: 0,
        total: urls.length,
        detail: `Hay ${v.unknown.length} sin datos de timing; comprobando caché HTTP…`,
      })
      const b = await verifyHttpCache(urls, { signal, onProgress })
      return { hits: b.hits, miss: b.miss, unknown: v.unknown }
    }
    return v
  }

  const onManualVerify = async () => {
    setVerifying(true)
    setVerifyError(null)
    setVerified(null)
    try {
      const v = await doVerify(urls, undefined, setVerifyProgress)
      setVerified(v)
    } catch (e) {
      console.error(e)
      setVerifyError(e?.message || 'Error al verificar')
    } finally {
      setVerifying(false)
      setVerifyProgress(null)
    }
  }

  const retryFailed = async () => {
    if (!failed.length) return
    setVerifyError(null)
    try {
      const { failed: f2 } = await warmup(failed, {
        cacheMode: 'reload',
        concurrency: 2,
        retries: 3,
        onTick: inc => setDone(d => d + inc),
      })
      setFailed(f2)
      const v = await doVerify(urls, undefined, setVerifyProgress)
      setVerified(v)
    } catch (e) {
      console.error(e)
      setVerifyError(e?.message || 'Error en reintento')
    } finally {
      setVerifyProgress(null)
    }
  }

  const forceWarm = async () => {
    setDone(0)
    setFailed([])
    setVerified(null)
    setVerifyError(null)
    try {
      const tick = inc => setDone(d => d + inc)
      const { failed } = isIPadSafari
        ? await warmupSafari(urls, tick, () => false)
        : await warmup(urls, {
            cacheMode: 'reload',
            concurrency: WARMUP_FETCH_CONCURRENCY,
            retries: 2,
            onTick: tick,
          })
      setFailed(failed)
      const v = await doVerify(urls, undefined, setVerifyProgress)
      setVerified(v)
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: VERSION,
        total,
        ok: failed.length === 0,
        finishedAt: new Date().toISOString()
      }))
    } catch (e) {
      console.error(e)
      setVerifyError(e?.message || 'Error al forzar recarga')
    } finally {
      setVerifyProgress(null)
    }
  }

  const saveSeal = () => {
    const hits = verified?.hits?.length ?? null
    const miss = verified?.miss?.length ?? null
    const unknown = verified?.unknown?.length ?? null
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: VERSION, total,
      ok: hits !== null ? hits >= Math.floor(total * 0.9) : null,
      hits, miss, unknown,
      finishedAt: new Date().toISOString()
    }))
    alert('Marcado: warmup completado en este dispositivo.')
  }

  const unknownInfo = verified?.unknown?.length
    ? ` · desconocidos (TAO/CORS): ${verified.unknown.length}`
    : ''

  return (
    <div style={{ fontFamily:'system-ui', padding:24, lineHeight:1.45, maxWidth:1000 }}>
      <div style={{ marginBottom:16 }}>
        <button
          type="button"
          onClick={() => navigate(ROUTES.HOME, { replace: false })}
          style={{
            padding:'8px 14px',
            cursor:'pointer',
            border:'1px solid #ccc',
            borderRadius:8,
            background:'#f8f8f8',
            fontSize:14,
          }}
        >
          ← Volver al inicio
        </button>
        <span style={{ marginLeft:12, fontSize:13, opacity:0.75 }}>
          (mismo acceso que el icono de la tablet; la precarga queda en esta sesión)
        </span>
      </div>
      <h1>Precargando vídeos…</h1>
      <p style={{ margin:'8px 0' }}>
        <strong>{done}</strong> / {total} completados
        {failed.length > 0 && <span> · Fallidos: {failed.length}</span>}
      </p>
      <progress max={total || 1} value={done} style={{ width:480, height:16 }} />
      {done >= total && !verified && verifyProgress && (
        <div style={{ marginTop:14, padding:12, background:'#f4f7fb', borderRadius:8, border:'1px solid #dde4ee', maxWidth:560 }}>
          <p style={{ margin:0, fontWeight:600, color:'#234' }}>
            {verifyProgress.phase === 'timing' && 'Verificación 1/2 — medición de red (caché)'}
            {verifyProgress.phase === 'http-cache' && 'Verificación 2/2 — comprobación directa en caché'}
          </p>
          <p style={{ margin:'8px 0 6px', fontSize:14, color:'#456' }}>
            {verifyProgress.detail}
          </p>
          {verifyProgress.total > 0 && (
            <>
              <progress
                max={verifyProgress.total}
                value={verifyProgress.current}
                style={{ width:'100%', height:14 }}
              />
              <p style={{ margin:'6px 0 0', fontSize:13, color:'#666' }}>
                <strong>{verifyProgress.current}</strong> / {verifyProgress.total} · cada petición corta a los {VERIFY_PER_URL_TIMEOUT_MS / 1000}s para no quedar colgado
              </p>
            </>
          )}
        </div>
      )}
      {done >= total && !verified && !verifyProgress && !verifyError && (
        <p style={{ marginTop:10, color:'#555' }}>
          Preparando verificación de caché…
        </p>
      )}
      {verifyError && (
        <div
          role="alert"
          style={{ marginTop:12, padding:12, background:'#fff3f0', border:'1px solid #e8b4a8', borderRadius:8, maxWidth:560 }}
        >
          <strong>Error:</strong> {verifyError}
          <p style={{ margin:'8px 0 0', fontSize:14 }}>
            Prueba «Verificar caché» o «Forzar recarga». Si usas muchos vídeos grandes, la verificación puede tardar varios minutos pero ya no debería quedarse bloqueada.
          </p>
        </div>
      )}
      {done >= total && verified && (
        <p style={{ marginTop:10 }}>
          ✓ Proceso terminado (descarga + comprobación). Ya puedes volver al inicio.
          <span style={{ display:'block', marginTop:6, fontSize:13, color:'#555', fontWeight:'normal' }}>
            La precarga evita bajar de red otra vez; al reproducir por primera vez el navegador puede seguir tardando un poco en decodificar y pintar — es normal.
          </span>
        </p>
      )}

      <div style={{ marginTop:16, display:'flex', flexWrap:'wrap', gap:8 }}>
        <button onClick={onManualVerify} disabled={verifying} style={{ padding:'8px 12px', cursor:'pointer' }}>
          {verifying ? 'Verificando…' : 'Verificar caché'}
        </button>
        <button onClick={retryFailed} disabled={!failed.length} style={{ padding:'8px 12px', cursor: failed.length ? 'pointer':'not-allowed' }}>
          Reintentar fallos ({failed.length})
        </button>
        <button onClick={forceWarm} style={{ padding:'8px 12px', cursor:'pointer' }}>
          Forzar recarga (red)
        </button>
        <button onClick={saveSeal} style={{ padding:'8px 12px', cursor:'pointer' }}>
          Guardar sello local
        </button>
      </div>

      {verified && (
        <div style={{ marginTop:18 }}>
          <h3>Resultado de verificación</h3>
          <p>
            HIT: {verified.hits?.length || 0}
            {' · '}MISS: {verified.miss?.length || 0}
            {unknownInfo}
          </p>

          {verified.hits?.length ? (
            <details open>
              <summary>HIT (en caché)</summary>
              <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
                <ul style={{ columns: 2, marginTop:8 }}>
                  {verified.hits.map(u => <li key={u}>✅ {u.split('/').pop()}</li>)}
                </ul>
              </div>
            </details>
          ) : null}

          {verified.miss?.length ? (
            <details open style={{ marginTop:8 }}>
              <summary>MISS (no encontrado en caché)</summary>
              <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
                <ul style={{ columns: 2, marginTop:8 }}>
                  {verified.miss.map(u => <li key={u}>❌ {u.split('/').pop()}</li>)}
                </ul>
              </div>
            </details>
          ) : null}

          {verified.unknown?.length ? (
            <details style={{ marginTop:8 }}>
              <summary>UNKNOWN (sin datos de timing — añade TAO)</summary>
              <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
                <ul style={{ columns: 2, marginTop:8 }}>
                  {verified.unknown.map(u => <li key={u}>❓ {u.split('/').pop()}</li>)}
                </ul>
              </div>
              <p style={{marginTop:8}}>
                Para que estos pasen a HIT/MISS, sirve los vídeos con
                <code> Timing-Allow-Origin: *</code>.
              </p>
            </details>
          ) : null}
        </div>
      )}

      <details style={{ marginTop:18 }}>
        <summary>Ver archivos (lista completa)</summary>
        <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
          <ul style={{ columns: 2, marginTop:8 }}>
            {urls.map(u => <li key={u}>{u.split('/').pop()}</li>)}
          </ul>
        </div>
      </details>

      {/* Test real: descomenta y prueba en Offline */}
      {/* <video src={urls[0]} controls width="480" style={{marginTop:16}} /> */}
    </div>
  )
}
